import type { BaseQueryFn } from '@reduxjs/toolkit/query'
import userGWFragments from '@shared/hooks/usegwFragments'
import { API_URL } from '../../app/services/config'
import axios from 'axios'
import type { AxiosRequestConfig } from 'axios'
import { getUserAuthTokenVerified } from '../../app/services/user'
import { Notify } from 'components/Notification'
import { LoginActionTypes } from 'store/types/login'
import { changeRoute } from 'store/actions/login'
import store from '../../../config/store'
import { AxiosError } from './axiosError'

enum StatusCode {
  Unauthorized = 401,
  Forbidden = 403,
  TooManyRequests = 429,
  InternalServerError = 500,
}

const initialHeaders: AxiosRequestConfig['headers'] = {
  Accept: 'application/json',
  auth_token: '',
  'Accept-Language':
    localStorage.getItem('preferredLanguage') === 'en'
      ? 'en_EN'
      : localStorage.getItem('preferredLanguage') === 'ru'
      ? 'ru_RU'
      : 'az_AZ',
}

function handleError(error) {
  const { status } = error

  switch (status) {
    case StatusCode.InternalServerError: {
      if (error.data && error.data.messages) {
        Notify(error.data.messages[0].MESSAGE)
      } else {
        Notify(error.data)
      }
      break
    }
    case StatusCode.Forbidden: {
      if (error.data.messages[0]) {
        Notify(error.data.messages[0].MESSAGE)
      }
      break
    }
    case StatusCode.Unauthorized: {
      localStorage.clear()
      store.dispatch({ type: LoginActionTypes.UNAUTH_USER })
      store.dispatch(changeRoute(0))
      window.location.reload()
      break
    }
    case StatusCode.TooManyRequests: {
      if (error.data.messages[0]) {
        Notify(error.data.messages[0].MESSAGE)
      }
      break
    }
  }

  return Promise.reject(error)
}

const injectToken = (config: AxiosRequestConfig): AxiosRequestConfig => {
  try {
    const auth_token = getUserAuthTokenVerified()
    if (auth_token != null) {
      config.headers.auth_token = `${auth_token}`
    }

    return config
  } catch (error) {
    throw new Error(error)
  }
}

export const axiosBaseQuery =
  (
    { baseUrl }: { baseUrl: string | null } = { baseUrl: null }
  ): BaseQueryFn<
    {
      isGw?: boolean
      file?: 'upload' | 'download' | 'none'
      skipGlobalError?: boolean
      url: string
      throwable?: boolean
      method?: AxiosRequestConfig['method']
      data?: AxiosRequestConfig['data']
      params?: AxiosRequestConfig['params']
      headers?: AxiosRequestConfig['headers']
    },
    unknown,
    unknown
  > =>
  async ({
    file = 'none',
    isGw = true,
    skipGlobalError = false,
    throwable = false,
    url,
    method,
    data,
    params,
    headers,
  }) => {
    const userFragments = userGWFragments()
    const BASE_URL = isGw
      ? `${API_URL}gateway/${userFragments.customerNo}/inquiry`
      : API_URL + url
    const language = localStorage.getItem('i18nextLng')

    const FILE_UPLOAD_URL = `${API_URL}gateway/${userFragments.customerNo}/file-upload`
    const FILE_DOWNLOAD_URL = `${API_URL}gateway/${userFragments.customerNo}/file-download`

    const FILE_URL = file === 'upload' ? FILE_UPLOAD_URL : FILE_DOWNLOAD_URL

    try {
      const http = axios.create({
        baseURL: file !== 'none' ? FILE_URL : baseUrl ?? BASE_URL,
        headers: {
          ...initialHeaders,
          ...headers,
        },
        withCredentials: false,
      })

      http.interceptors.request.use(
        (value) => {
          if (throwable) {
            throw new Error('Test error case')
          }
          return injectToken(value)
        },
        (error) => Promise.reject(error)
      )

      http.interceptors.response.use(
        (response) => {
          if (response.status === 204) {
            throw new AxiosError(
              {
                az: 'Təəssüf ki, məlumat əldə etmək alınmadı. Zəhmət olmasa, təkrarən cəhd edin.',
                ru: 'К сожалению, не удалось получить информацию. Пожалуйста, попробуйте повторно.',
                en: 'Unfortunately, the information was not obtained. Please, try again.',
              }[language],
              {
                response: {
                  ...response.data,
                  status: 500,
                },
                config: {},
                request: undefined,
              }
            )
          }

          const statusCode = isGw
            ? response?.data?.data?.status ||
              response?.data?.data?.code ||
              response?.status
            : response?.status

          const message = isGw
            ? response?.data?.data?.message ||
              response?.data?.data?.error ||
              response?.statusText
            : response?.statusText

          if (statusCode !== 200) {
            throw new AxiosError(message, {
              response: {
                ...response.data.data,
                status: statusCode,
              },
              config: {},
              request: undefined,
            })
          }

          if(file === 'download') {
            return response
          }

          return isGw ? response.data.data : response
        },
        (error) => {
          const { response } = error
          return handleError(response)
        }
      )

      if (file === 'upload') {
        data.append('meta', JSON.stringify({ url }))

        const { data: resData, ...rest } = await http({
          method,
          data,
          params,
        })

        return { data: resData, meta: { ...rest, skipGlobalError } }
      }

      if (file === 'download') {
        // IMPORTANT: yalnızca { url } gönder
        const resp = await http({
          method: method ?? 'POST',
          data: { url }, // /file-download proxy’si böyle bekliyor
          params,
          responseType: 'blob', // ⬅⬅⬅ kritik
        })

        // RTK Query "data" dönmeli; blob + headerları ile dönelim
        return {
          data: {
            blob: resp.data, // Blob (image/png vs)
            headers: resp.headers, // Content-Type, Content-Disposition...
            status: resp.status,
          },
          meta: { ...resp, skipGlobalError },
        }
      }

      const resp = await http({
        method,
        data: {
          url,
          data: {
            ...userFragments,
            ...data,
          },
        },
        params,
      })

      const resData = resp?.data ?? null
      const rest = resp ? { ...resp } : {}

      return { data: resData, meta: { ...rest, skipGlobalError } }
    } catch (axiosError) {
      const err = axiosError as AxiosError

      return {
        meta: { skipGlobalError },
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      }
    }
  }
